import React, { useRef, useState, useEffect } from 'react';
import { Input, InputProps } from './input';
import { Button } from './button';
import { cn } from '@/lib/utils';
import { Highlighter, Type } from 'lucide-react';

interface HighlightableInputProps extends InputProps {
  hint?: string;
  unit?: string;
  onHighlightChange?: (hasHighlights: boolean) => void;
}

export function HighlightableInput({ 
  className, 
  hint,
  unit,
  value: propValue,
  onChange,
  onHighlightChange,
  ...props 
}: HighlightableInputProps) {
  const ref = useRef<HTMLInputElement>(null);
  const [hasHighlights, setHasHighlights] = useState(false);
  
  // Parse the value to handle both plain text and HTML with marks
  const parseContent = (content: string): { text: string; html: string } => {
    // If it contains HTML marks, extract text
    if (content && content.includes('<mark')) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      return {
        text: tempDiv.textContent || '',
        html: content
      };
    }
    // Otherwise, return as plain text (no legacy ==...== support)
    return {
      text: content || '',
      html: content || ''
    };
  };

  const [internalValue, setInternalValue] = useState(() => {
    const parsed = parseContent(String(propValue || ''));
    return parsed.text;
  });
  
  const [htmlValue, setHtmlValue] = useState(() => {
    const parsed = parseContent(String(propValue || ''));
    return parsed.html;
  });

  // Update when prop value changes
  useEffect(() => {
    if (propValue !== undefined) {
      const parsed = parseContent(String(propValue));
      setInternalValue(parsed.text);
      setHtmlValue(parsed.html);
      checkForHighlights(parsed.html);
    }
  }, [propValue]);

  // Check if content has highlights
  const checkForHighlights = (html: string) => {
    const hasMarks = html.includes('<mark>');
    setHasHighlights(hasMarks);
    onHighlightChange?.(hasMarks);
  };

  const highlightSelection = () => {
    const el = ref.current;
    if (!el) return;
    
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const value = el.value || '';
    
    // If no value exists, nothing to highlight
    if (!value) return;
    
    let newHtml: string;
    
    // If no text is selected (start === end), highlight the entire value
    if (start === end) {
      newHtml = `<mark>${value}</mark>`;
    } else {
      // Partial selection - add highlight to selected portion
      const selectedText = value.slice(start, end);
      if (!selectedText) return;
      
      const before = value.slice(0, start);
      const after = value.slice(end);
      newHtml = `${before}<mark>${selectedText}</mark>${after}`;
    }
    
    setHtmlValue(newHtml);
    checkForHighlights(newHtml);
    
    // Call onChange with the HTML value
    onChange?.({ target: { value: newHtml } } as any);
    
    // Keep focus on input
    requestAnimationFrame(() => {
      if (ref.current) {
        ref.current.focus();
      }
    });
  };

  const clearHighlights = () => {
    const el = ref.current;
    if (!el) return;
    const value = el.value || '';
    
    setHtmlValue(value);
    setInternalValue(value);
    checkForHighlights(value);
    
    onChange?.({ target: { value } } as any);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    
    // Preserve highlights if they exist, otherwise use plain text
    const newHtml = hasHighlights && htmlValue.includes('<mark>') 
      ? htmlValue.replace(/>([^<]*)</g, `>${newValue}<`)
      : newValue;
    
    setHtmlValue(newHtml);
    onChange?.({ target: { value: newHtml } } as any);
  };

  return (
    <div className="space-y-1.5">
      <div className="relative">
        <Input 
          ref={ref} 
          className={cn(
            className,
            hasHighlights && "border-yellow-400 bg-yellow-50/50 dark:bg-yellow-950/20"
          )} 
          value={internalValue}
          onChange={handleInputChange}
          {...props} 
        />
        {unit && (
          <div className="absolute right-10 top-1/2 -translate-y-1/2 pointer-events-none text-sm text-muted-foreground font-medium z-10">
            {unit}
          </div>
        )}
        {hasHighlights && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none z-20">
            <Highlighter className="h-4 w-4 text-yellow-600" />
          </div>
        )}
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <Button 
            type="button" 
            size="sm" 
            variant={hasHighlights ? "default" : "outline"}
            onClick={highlightSelection}
            onMouseDown={(e) => e.preventDefault()}
            className={cn(
              hasHighlights && "bg-yellow-500 hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-700"
            )}
            title="Click to highlight entire value, or select text first to highlight just that portion"
          >
            <Highlighter className="h-3 w-3 mr-1" />
            Highlight
          </Button>
          {hasHighlights && (
            <Button 
              type="button" 
              size="sm" 
              variant="ghost" 
              onClick={clearHighlights}
              onMouseDown={(e) => e.preventDefault()}
            >
              <Type className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
        <span className="flex items-center gap-1">
          {hasHighlights && <Highlighter className="h-3 w-3 text-yellow-600" />}
          {hint || 'Click Highlight to mark for PDF'}
        </span>
      </div>
    </div>
  );
}
